import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const LABEL = {
  RESERVATION_STATUS_MISMATCH: "Reservation status ≠ its stays",
  ROOM_MULTIPLE_LIVE_STAYS: "Room with more than one live stay",
  LIVE_STAY_DELETED: "Checked-in stay marked deleted",
};

/** FIX 8 · flags from the nightly PMS consistency sweep. */
export function PmsConsistencyCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (rescan = false) => {
    setBusy(true);
    try { const { data } = await api.get("/admin/system-health/pms-consistency", { params: rescan ? { rescan: true } : {} }); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load PMS consistency"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flags = data?.flags || [];
  const ok = data && flags.length === 0;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-5" data-testid="pms-consistency-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[14px] font-semibold text-slate-900 inline-flex items-center gap-2">
            {ok ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <ShieldAlert className="w-4 h-4 text-amber-600" />} PMS consistency
          </h3>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Nightly sweep (03:00 IST) · last run {data?.last_run?.scanned_at ? new Date(data.last_run.scanned_at).toLocaleString() : (data?.scanned_at ? new Date(data.scanned_at).toLocaleString() : "never")}
          </p>
        </div>
        <button onClick={() => load(true)} disabled={busy} data-testid="pms-consistency-rescan"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12.5px] font-medium hover:bg-slate-50 disabled:opacity-60">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Rescan now
        </button>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {Object.entries(LABEL).map(([type, label]) => {
          const n = flags.filter((f) => f.type === type).length;
          return (
            <div key={type} className={`rounded-xl border p-3 ${n ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-slate-50"}`} data-testid={`pms-flag-${type}`}>
              <div className="text-[22px] font-semibold tabular-nums">{n}</div>
              <div className="text-[12px] text-slate-600">{label}</div>
            </div>
          );
        })}
      </div>
      {flags.length > 0 && (
        <details className="mt-3" data-testid="pms-consistency-list">
          <summary className="text-[12.5px] text-slate-600 cursor-pointer">Show {flags.length} flag{flags.length > 1 ? "s" : ""}</summary>
          <ul className="mt-2 space-y-1 text-[12.5px] font-mono text-slate-700">
            {flags.slice(0, 100).map((f, i) => (
              <li key={f.id || i}>{f.type} · {f.code || f.room_id || f.stay_id} {f.actual ? `· ${f.actual} → ${f.expected}` : ""} {f.codes ? `· ${f.codes.join(", ")}` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
